import React, { useEffect, useState } from 'react';
import { Button, ToastMessage } from './ui';
import {
  fetchHoldReviewQuestions,
  completeHoldReview,
  errorMessageFrom,
  HoldReviewQuestion,
  HoldReviewAnswerValue,
  HoldReviewOutcome,
} from '../api/cancellationRingfence';

/* ------------------------------------------------------------- hold review */

interface HoldReviewSectionProps {
  taskId: string;
  canAct: boolean;
  reasons: import('../api/cancellationRingfence').WaiverReasonOption[];
  requestedReason: string | null;
  /** For substituting Q6's "[email address on file]" placeholder at display
   * time. Absent (null) leaves the placeholder verbatim -- never invented. */
  clientEmail: string | null;
  showToast?: (toast: ToastMessage) => void;
  onChanged?: () => void;
}

interface DraftAnswer {
  answer?: HoldReviewAnswerValue;
  note: string;
}

const ANSWER_OPTIONS: Array<{ value: HoldReviewAnswerValue; label: string }> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'na', label: 'N/A' },
];

/** Served literally by the backend on a 'text' question (Q6) -- the panel
 * substitutes the real client email for display only; the raw question
 * text (with the placeholder intact) is still what travels in the memo,
 * since the backend does its own substitution server-side for the timeline
 * record. Must match `hold_review_service.EMAIL_PLACEHOLDER` exactly. */
const EMAIL_PLACEHOLDER = '[email address on file]';

export const HoldReviewSection: React.FC<HoldReviewSectionProps> = ({
  taskId, canAct, reasons, requestedReason, clientEmail, showToast, onChanged,
}) => {
  const [questions, setQuestions] = useState<HoldReviewQuestion[] | null>(null);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<number, DraftAnswer>>({});
  const [overallNote, setOverallNote] = useState('');
  const [waiverReason, setWaiverReason] = useState('');
  const [submitting, setSubmitting] = useState<HoldReviewOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    // Every value below belongs to one client/task. This component is reused
    // when the lane selection changes, so leaving React's local state intact
    // would show the previous client's answers/result on the next task and
    // could submit those answers under the new task id.
    setQuestions(null);
    setQuestionsError(null);
    setAnswers({});
    setOverallNote('');
    setWaiverReason('');
    setSubmitting(null);
    setError(null);
    setDone(null);

    let cancelled = false;
    fetchHoldReviewQuestions()
      .then((qs) => { if (!cancelled) setQuestions(qs); })
      .catch(() => { if (!cancelled) setQuestionsError('Could not load the review questions.'); });
    return () => { cancelled = true; };
  }, [taskId]);

  const setAnswer = (number: number, patch: Partial<DraftAnswer>) =>
    setAnswers((prev) => {
      const existing: DraftAnswer = prev[number] || { note: '' };
      return { ...prev, [number]: { ...existing, ...patch } };
    });

  // Never hard-code the question count: a 'choice' question is satisfied by
  // a picked answer, a 'text' question by a non-empty trimmed note. Derived
  // entirely from the fetched list length, per the 18 Aug amendment (the
  // script grew from 7 to 9 once and will change wording/count again).
  const allAnswered = !!questions
    && questions.length > 0
    && questions.every((q) => {
      const draft = answers[q.number];
      if (q.answerType === 'text') return !!draft?.note?.trim();
      return !!draft?.answer;
    });

  const submit = async (outcome: HoldReviewOutcome) => {
    if (!questions || !allAnswered || submitting || done) return;
    setSubmitting(outcome);
    setError(null);
    try {
      const response = await completeHoldReview(taskId, {
        answers: questions.map((q) => {
          if (q.answerType === 'text') {
            // The server REJECTS a non-null answer on a text question -- the
            // free-text response travels only in `note`.
            return { question: q.number, answer: null, note: answers[q.number]!.note.trim() };
          }
          return {
            question: q.number,
            answer: answers[q.number]!.answer!,
            note: answers[q.number]!.note.trim() || undefined,
          };
        }),
        overallNote: overallNote.trim() || undefined,
        outcome,
        waiverReason: outcome === 'cancel_waive' ? waiverReason : undefined,
      });
      setDone(response.message);
      if (response.warnings.length > 0) {
        // Non-blocking: the server has already verified the outcome and
        // completed the task by the time this response comes back. A
        // warning means a best-effort follow-up (fee-waiver record, a
        // per-claim timeline memo) did not land and needs manual attention
        // -- it is not a reason to treat the submit as failed.
        showToast?.({ type: 'error', message: `Recorded, but: ${response.warnings.join(' ')}` });
      } else {
        showToast?.({ type: 'success', message: response.message });
      }
      // The SERVER completed the task and cleared the holds -- just refetch.
      onChanged?.();
    } catch (err) {
      setError(errorMessageFrom(err, 'Failed to record the review — the task is still open.'));
      setSubmitting(null);
      return;
    }
    setSubmitting(null);
  };

  return (
    <div className="ringfence__hold-review">
      {requestedReason && (
        <p className="ringfence__banner">
          Client's stated reason for cancelling: {requestedReason}
        </p>
      )}
      <p className="ringfence__banner">
        Phone the client, work through the script below, then record the outcome.
      </p>

      {questionsError && <div className="ringfence__error">{questionsError}</div>}
      {!questions && !questionsError && <div>Loading questions…</div>}

      {questions && questions.map((q) => {
        const displayText = q.answerType === 'text' && clientEmail
          ? q.text.replace(EMAIL_PLACEHOLDER, clientEmail)
          : q.text;
        return (
          <fieldset key={q.number} className="ringfence__question" data-testid={`hold-question-${q.number}`}>
            <legend>{q.number}. {displayText}</legend>
            {q.answerType === 'text' ? (
              <textarea
                className="lane-modal__input"
                aria-label={`Answer to question ${q.number}`}
                required
                rows={2}
                value={answers[q.number]?.note || ''}
                disabled={!canAct || !!done}
                onChange={(e) => setAnswer(q.number, { note: e.target.value })}
                placeholder="Client's answer, in their own words"
              />
            ) : (
              <>
                <div role="radiogroup" aria-label={`Answer to question ${q.number}`}>
                  {ANSWER_OPTIONS.map((o) => (
                    <label key={o.value}>
                      <input
                        type="radio"
                        name={`hold-q-${q.number}`}
                        value={o.value}
                        checked={answers[q.number]?.answer === o.value}
                        disabled={!canAct || !!done}
                        onChange={() => setAnswer(q.number, { answer: o.value })}
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
                <input
                  type="text"
                  className="lane-modal__input"
                  aria-label={`Note for question ${q.number} (optional)`}
                  placeholder="Note (optional)"
                  value={answers[q.number]?.note || ''}
                  disabled={!canAct || !!done}
                  onChange={(e) => setAnswer(q.number, { note: e.target.value })}
                />
              </>
            )}
          </fieldset>
        );
      })}

      <div className="lane-modal__field">
        <label htmlFor="hold-overall-note">Overall note (optional)</label>
        <textarea
          id="hold-overall-note"
          className="lane-modal__input"
          rows={3}
          value={overallNote}
          disabled={!canAct || !!done}
          onChange={(e) => setOverallNote(e.target.value)}
          placeholder="What was agreed on the call?"
        />
      </div>

      {error && <div className="ringfence__error" role="alert">{error}</div>}
      {done && <div className="ringfence__outcome" role="status">{done}</div>}

      {canAct && !done && (
        <div className="ringfence__actions">
          {/* Mark's steer first: "We should try and keep the claim if possible." */}
          <Button
            variant="primary"
            size="sm"
            disabled={!allAnswered || !!submitting}
            onClick={() => submit('keep')}
          >
            Don't cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!allAnswered || !!submitting}
            onClick={() => submit('cancel_charge')}
          >
            Cancel + charge
          </Button>
          <span className="ringfence__waive-group">
            <select
              className="lane-modal__input"
              aria-label="Waiver reason"
              value={waiverReason}
              disabled={!!submitting}
              onChange={(e) => setWaiverReason(e.target.value)}
            >
              <option value="">Waiver reason…</option>
              {reasons.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
            <Button
              variant="secondary"
              size="sm"
              disabled={!allAnswered || !waiverReason || !!submitting}
              onClick={() => submit('cancel_waive')}
            >
              Cancel + waive
            </Button>
          </span>
          {submitting && <span>Recording…</span>}
        </div>
      )}
    </div>
  );
};
